import { useState, createContext, useContext, useMemo, useEffect } from "react";
import apisService from "api/services/apiService";
import { useSetRecoilState } from "recoil";
import { currentTransactionTrackerRegion } from "data/Atoms";
import { useCustomQuery } from "hooks";
import { getLocalStorage } from "utils/localStorageUtils";

const TransactionsTrackerContext = createContext();

export const TransactionsTrackerContextProvider = ({ children }) => {
  const [viewType, setViewType] = useState(null);
  const [showData, setShowData] = useState(false);
  const [showTransactionDetails, setShowTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [selectedParcel, setSelectedParcel] = useState(null);
  const [parcelData, setParcelData] = useState({});
  const [parcelTransactionsData, setParcelTransactionsData] = useState({});
  const [selectedSubdivisions, setSelectedSubdivisions] = useState([]);
  const [loadingSubdivisionsMapParcels, setLoadingSubdivisionsMapParcels] =
    useState(false);
  const [mapNotes, setMapNotes] = useState([]);
  const [notesCount, setNotesCount] = useState([]);
  const [loadingMapNotes, setLoadingMapNotes] = useState(false);
  const [loadingTransactionData, setLoadingTransactionData] = useState(false);

  const setRegion = useSetRecoilState(currentTransactionTrackerRegion);

  const organizationsId = getLocalStorage("organizationId");

  // ✅ Transactins Regions Query
  const { data: transactionTrackerRegions, isSuccess } = useCustomQuery({
    keys: ["transactions-tracker-regions", String(organizationsId)],
    fetchFn: (_, cancelToken) =>
      apisService.booking.getTransactionTrackerRegions(
        organizationsId,
        cancelToken
      ),
    options: { enabled: !!organizationsId && organizationsId != "undefined" },
  });

  useEffect(() => {
    if (isSuccess) {
      const params = new URLSearchParams(window.location.search);
      const regionParam = params.get("region");

      // Try to find region by key (assuming each region has a `key` field)
      const matchedRegion = transactionTrackerRegions?.find(
        (r) => r.key.toLowerCase() === regionParam?.toLowerCase()
      );

      if (matchedRegion) {
        setRegion(matchedRegion);
      } else {
        // no region param, fall back to default (first one)
        setRegion(transactionTrackerRegions?.[0]);
      }
    }
  }, [isSuccess]);

  const value = useMemo(
    () => ({
      viewType,
      setViewType,
      showData,
      setShowData,
      showTransactionDetails,
      setShowTransactionDetails,
      selectedParcel,
      setSelectedParcel,
      parcelData,
      setParcelData,
      parcelTransactionsData,
      setParcelTransactionsData,
      selectedTransaction,
      setSelectedTransaction,
      selectedSubdivisions,
      setSelectedSubdivisions,
      mapNotes,
      setMapNotes,
      notesCount,
      setNotesCount,
      loadingMapNotes,
      setLoadingMapNotes,
      loadingSubdivisionsMapParcels,
      setLoadingSubdivisionsMapParcels,
      loadingTransactionData,
      setLoadingTransactionData,
      transactionTrackerRegions,
    }),
    [
      viewType,
      setViewType,
      showData,
      setShowData,
      showTransactionDetails,
      setShowTransactionDetails,
      selectedParcel,
      setSelectedParcel,
      parcelData,
      setParcelData,
      parcelTransactionsData,
      setParcelTransactionsData,
      selectedTransaction,
      setSelectedTransaction,
      selectedSubdivisions,
      setSelectedSubdivisions,
      mapNotes,
      setMapNotes,
      notesCount,
      setNotesCount,
      loadingMapNotes,
      setLoadingMapNotes,
      loadingSubdivisionsMapParcels,
      setLoadingSubdivisionsMapParcels,
      loadingTransactionData,
      setLoadingTransactionData,
      transactionTrackerRegions,
    ]
  );

  return (
    <TransactionsTrackerContext.Provider value={value}>
      {children}
    </TransactionsTrackerContext.Provider>
  );
};

export const useTransactionsTrackerContext = () => {
  return useContext(TransactionsTrackerContext);
};
